import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { format, differenceInDays, isSameDay, isWithinInterval, startOfDay } from 'date-fns';

const DateRangePicker = ({ open, onClose, onDateRangeChange, selectedDate }) => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState('');

    // ✅ Reset when dialog opens
    useEffect(() => {
        if (open) {
            setStartDate(null);
            setEndDate(null);
            setError('');
        }
    }, [open]);

    // ✅ Handle date selection
    const handleDateChange = (newDate) => {
        setError('');

        const normalizedNewDate = startOfDay(newDate);

        // First click - set start date
        if (!startDate) {
            setStartDate(normalizedNewDate);
            setEndDate(null);
            return;
        }

        // Second click - set end date
        if (!endDate) {
            let finalStartDate = startDate;
            let finalEndDate = normalizedNewDate;

            // Ensure endDate is after startDate
            if (normalizedNewDate < startDate) {
                finalStartDate = normalizedNewDate;
                finalEndDate = startDate;
            }

            // Validate range (max 5 days)
            const daysDiff = Math.abs(differenceInDays(finalEndDate, finalStartDate));

            if (daysDiff > 4) { // 0-4 days difference = max 5 days
                setError('Please select a date range of maximum 5 days');
                return;
            }

            setStartDate(finalStartDate);
            setEndDate(finalEndDate);
            return;
        }

        // Third click - reset and start new selection
        setStartDate(normalizedNewDate);
        setEndDate(null);
    };

    // ✅ Apply selected range
    const handleApply = () => {
        if (!startDate) {
            setError('Please select at least one date');
            return;
        }

        // If only start date selected, use it as single day
        const finalEndDate = endDate || startDate;

        onDateRangeChange(startDate, finalEndDate);
        onClose();
    };

    // ✅ Handle cancel
    const handleCancel = () => {
        setStartDate(null);
        setEndDate(null);
        setError('');
        onClose();
    };

    // ✅ Get selected days count
    const getSelectedDaysCount = () => {
        if (!startDate) return 0;
        if (!endDate) return 1;
        return differenceInDays(endDate, startDate) + 1;
    };

    // ✅ Custom day rendering component
    const CustomDay = (props) => {
        const { day, outsideCurrentMonth, ...other } = props;

        if (!day) return <PickersDay {...props} />;

        const normalizedDay = startOfDay(day);
        const normalizedStart = startDate ? startOfDay(startDate) : null;
        const normalizedEnd = endDate ? startOfDay(endDate) : null;

        // Check if day is in selected range
        const isStart = normalizedStart && isSameDay(normalizedDay, normalizedStart);
        const isEnd = normalizedEnd && isSameDay(normalizedDay, normalizedEnd);
        const isInRange = normalizedStart && normalizedEnd &&
            isWithinInterval(normalizedDay, { start: normalizedStart, end: normalizedEnd });

        return (
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {/* Range background highlight */}
                {isInRange && !outsideCurrentMonth && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: isStart ? '50%' : 0,
                            right: isEnd ? '50%' : 0,
                            backgroundColor: '#DBEAFE',
                            zIndex: 0
                        }}
                    />
                )}

                {/* The actual day button */}
                <PickersDay
                    {...other}
                    day={day}
                    outsideCurrentMonth={outsideCurrentMonth}
                    sx={{
                        zIndex: 1,
                        fontWeight: (isStart || isEnd) ? 600 : 400,
                        backgroundColor: (isStart || isEnd) ? '#2563EB !important' : 'transparent',
                        color: (isStart || isEnd) ? 'white !important' : 'inherit',
                        '&:hover': {
                            backgroundColor: (isStart || isEnd) ? '#1D4ED8 !important' : '#F3F4F6'
                        },
                        '&.Mui-selected': {
                            backgroundColor: '#2563EB !important',
                            color: 'white !important'
                        }
                    }}
                />
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle>
                <Typography variant="h6" fontWeight={600}>
                    Select Date Range
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Select up to 5 consecutive days (including weekends)
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* Error Message */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Selected Range Info */}
                <Box
                    sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: '#F3F4F6',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="body2" fontWeight={500} color="text.secondary" mb={1}>
                        Selected Range:
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Start Date
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {startDate ? format(startDate, 'dd MMM yyyy') : '-'}
                            </Typography>
                        </Box>
                        <Typography variant="h6" color="text.secondary">→</Typography>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                End Date
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {endDate ? format(endDate, 'dd MMM yyyy') : '-'}
                            </Typography>
                        </Box>
                        <Box ml="auto">
                            <Typography variant="caption" color="text.secondary">
                                Days
                            </Typography>
                            <Typography variant="h6" fontWeight={600} color="primary">
                                {getSelectedDaysCount()}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Calendar */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateCalendar
                        value={startDate}
                        onChange={handleDateChange}
                        sx={{
                            width: '100%',
                            maxHeight: 'none',
                            '& .MuiPickersCalendarHeader-root': {
                                paddingLeft: 2,
                                paddingRight: 2
                            },
                            '& .MuiDayCalendar-weekContainer': {
                                margin: 0
                            }
                        }}
                        slots={{
                            day: CustomDay
                        }}
                    />
                </LocalizationProvider>

                {/* Instructions */}
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#EFF6FF', borderRadius: 1 }}>
                    <Typography variant="caption" color="primary" display="block" mb={0.5}>
                        📌 How to select:
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        1. Click on a start date<br />
                        2. Click on an end date (max 5 days from start)<br />
                        3. Click again to reset and start new selection
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, gap: 1 }}>
                <Button
                    onClick={handleCancel}
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleApply}
                    disabled={!startDate}
                    sx={{ textTransform: 'none' }}
                >
                    Apply Range ({getSelectedDaysCount()} {getSelectedDaysCount() === 1 ? 'day' : 'days'})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DateRangePicker;